import React, {useCallback, useMemo} from 'react';
import {GatewayGetSchema} from '../../../../api/DBSchema/Gateway';
import LockToPlanContainer from '../../../../components/Editor/LockToPlanContainer';
import SMCLabel from '../../../../components/Simple/SMCLabel';
import Indentation from '../../../../components/Simple/Indentation';
import {SMCRadioGroup, SMCRadio} from '../../../../components/Simple/SMCRadio';
import {SMCSelect, SMCOption} from '../../../../components/Simple/SMCSelect';
import {useTranslation} from 'react-i18next';
import {FirmwareGetListSchema} from '../../../../api/DBSchema/Firmware';
import EmptyRow from '../../../../components/Simple/EmptyRow';
import EditorLayout from '../../../../components/Layouts/EditorLayout';
import {SMCDisabledProvider} from '../../../../components/Simple/SMCDisabledContext';
import Sepperator from '../../../../components/Simple/Sepperator';
import SMCTextInput from '../../../../components/Simple/SMCTextInput';
import {firmwareIcon} from "../../../../assets/images/headerIcons";

const pageSchema = GatewayGetSchema.pages.firmware;
const pageId = pageSchema.pageId;

function GatewayFirmware({
    gatewayObject,
    planObject,
    availableFirmwares,
    setGatewayData
}) {
    const { t } = useTranslation();
    
    const gatewayPage = gatewayObject[pageId];
    const planPage = planObject[pageId];
    const lockedToPlan = gatewayPage[pageSchema.lockedToPlan];

    let currentPageData;
    if (lockedToPlan) {
        currentPageData = planPage;
    } else {
        currentPageData = gatewayPage;
    }

    const gatewayGeneralPage = gatewayObject[GatewayGetSchema.pages.general.pageId];
    const reportedFirmware = gatewayGeneralPage?.[GatewayGetSchema.pages.general.reportedFirmware];

    const mode = currentPageData[pageSchema.mode];
    const firmware = currentPageData[pageSchema.firmware];
    const firmware1200 = currentPageData[pageSchema.firmware1200];
    const firmwareS1S2 = currentPageData[pageSchema.firmwareS1S2];
    const firmwareV0V1 = currentPageData[pageSchema.firmwareV0V1];
    const firmwareV2V3 = currentPageData[pageSchema.firmwareV2V3];
    const time = currentPageData[pageSchema.time];
    const weekDay = currentPageData[pageSchema.weekDay];
    const monthDay = currentPageData[pageSchema.monthDay];
    const frequency = currentPageData[pageSchema.frequency];
    
    const modeValues = pageSchema.modeValues;
    const frequencyValues = pageSchema.frequencyValues;

    const setPageValue = useCallback((param, newValue) => {
        setGatewayData([pageId, param], newValue);
    }, [setGatewayData]);

    const onToggleLockedToPlan = useCallback(locked => {
        // If unlocked page from plan, set all gateway fields to plan's fields
        if (!locked) {
            setGatewayData([pageId, pageSchema.mode], planPage[pageSchema.mode]);
            setGatewayData([pageId, pageSchema.firmware], planPage[pageSchema.firmware]);
            setGatewayData([pageId, pageSchema.firmware1200], planPage[pageSchema.firmware1200]);
            setGatewayData([pageId, pageSchema.firmwareS1S2], planPage[pageSchema.firmwareS1S2]);
            setGatewayData([pageId, pageSchema.firmwareV0V1], planPage[pageSchema.firmwareV0V1]);
            setGatewayData([pageId, pageSchema.firmwareV2V3], planPage[pageSchema.firmwareV2V3]);
            setGatewayData([pageId, pageSchema.weekDay], planPage[pageSchema.weekDay]);
            setGatewayData([pageId, pageSchema.monthDay], planPage[pageSchema.monthDay]);
            setGatewayData([pageId, pageSchema.time], planPage[pageSchema.time]);
            setGatewayData([pageId, pageSchema.frequency], planPage[pageSchema.frequency]);
        }
        setGatewayData([pageId, pageSchema.lockedToPlan], locked);

    }, [setGatewayData, planPage]);

    // Options of 00:00-01:00 until 23:00-00:00
    const timeOptions = useMemo(() => (
        [...Array(24)].map((e,i) => (
            <SMCOption key={i} value={i}>{i<10?'0':''}{i}:00-{((i+1)%24)<10?'0':''}{(i+1)%24}:00</SMCOption>
        ))
    ), []);

    const dayOfWeekOptions = useMemo(() => (
        <>
        <SMCOption value={1}>{t('SMP.SUNDAY')}</SMCOption>
        <SMCOption value={2}>{t('SMP.MONDAY')}</SMCOption>
        <SMCOption value={3}>{t('SMP.TUESDAY')}</SMCOption>
        <SMCOption value={4}>{t('SMP.WEDNESDAY')}</SMCOption>
        <SMCOption value={5}>{t('SMP.THURSDAY')}</SMCOption>
        <SMCOption value={6}>{t('SMP.FRIDAY')}</SMCOption>
        <SMCOption value={7}>{t('SMP.SATURDAY')}</SMCOption>
        </>
    ), [t]);

    const dayOfMonthOptions = useMemo(() => (
        [...Array(31)].map((e,i) => (
            <SMCOption key={i+1} value={i+1}>{i+1}</SMCOption>
        ))
    ), [])

    return (
        <LockToPlanContainer
            locked={lockedToPlan}
            onToggleLocked={onToggleLockedToPlan}
            labelText={t('SMC_GATEWAY_FIRM')}
            icon={firmwareIcon}
        >
            <EditorLayout labelWidth={240}>
                <SMCLabel bold>{t('SMP.GATEWAYS.PAGE.SERVICES.FIRMWARE.LINE1')}</SMCLabel>
                <Indentation>
                    <SMCRadioGroup value={mode} onChange={e => setPageValue(pageSchema.mode, e.target.value)}>
                        <SMCRadio value={modeValues.specific} label={t('SMP.GATEWAYS.PAGE.SERVICES.FIRMWARE.LINE2')} />
                        <Indentation>
                            {/* Disable fimrware choice if mode isn't specific */}
                            <SMCDisabledProvider forceDisable={mode !== modeValues.specific || lockedToPlan}>
                                {/* 600/1100 Firmware */}
                                <SMCSelect width={350} value={firmware} onChange={e => setPageValue(pageSchema.firmware, e.target.value)} label={t('SMP.GATEWAYS.PAGE.SERVICES.FIRMWARE.600_1000_APPLIANCE')}>
                                    <SMCOption value="no firmware configured">Do_Not_Upgrade</SMCOption>
                                    {
                                        availableFirmwares?.firmware600List?.map(firm => (
                                            <SMCOption value={firm[FirmwareGetListSchema.name].toLowerCase()} key={firm[FirmwareGetListSchema.name]}>{firm[FirmwareGetListSchema.name]}</SMCOption>
                                        ))
                                    }
                                </SMCSelect>
                                {/* 1200R Firmware */}
                                <SMCSelect width={350} value={firmware1200} onChange={e => setPageValue(pageSchema.firmware1200, e.target.value)} label={t('SMP.GATEWAYS.PAGE.SERVICES.FIRMWARE.1200R_APPLIANCE')}>
                                    <SMCOption value="no firmware configured">Do_Not_Upgrade</SMCOption>
                                    {
                                        availableFirmwares?.firmware1200List?.map(firm => (
                                            <SMCOption value={firm[FirmwareGetListSchema.name].toLowerCase()} key={firm[FirmwareGetListSchema.name]}>{firm[FirmwareGetListSchema.name]}</SMCOption>
                                        ))
                                    }
                                </SMCSelect>
                                {/* S1S2 Firmware */}
                                <SMCSelect width={350} value={firmwareS1S2} onChange={e => setPageValue(pageSchema.firmwareS1S2, e.target.value)} label={t('SMP.GATEWAYS.PAGE.SERVICES.FIRMWARE.700_1400_APPLIANCE')}>
                                    <SMCOption value="no firmware configured">Do_Not_Upgrade</SMCOption>
                                    {
                                        availableFirmwares?.firmwareS1S2List?.map(firm => (
                                            <SMCOption value={firm[FirmwareGetListSchema.name].toLowerCase()} key={firm[FirmwareGetListSchema.name]}>{firm[FirmwareGetListSchema.name]}</SMCOption>
                                        ))
                                    }
                                </SMCSelect>
                                {/* V0V1 Firmware */}
                                <SMCSelect width={350} value={firmwareV0V1} onChange={e => setPageValue(pageSchema.firmwareV0V1, e.target.value)} label={t('SMP.GATEWAYS.PAGE.SERVICES.FIRMWARE.1500_1500R_APPLIANCE')}>
                                    <SMCOption value="no firmware configured">Do_Not_Upgrade</SMCOption>
                                    {
                                        availableFirmwares?.firmwareV0V1List?.map(firm => (
                                            <SMCOption value={firm[FirmwareGetListSchema.name].toLowerCase()} key={firm[FirmwareGetListSchema.name]}>{firm[FirmwareGetListSchema.name]}</SMCOption>
                                        ))
                                    }
                                </SMCSelect>
                                {/* V2V3 Firmware */}
                                <SMCSelect width={350} value={firmwareV2V3} onChange={e => setPageValue(pageSchema.firmwareV2V3, e.target.value)} label={t('SMP.GATEWAYS.PAGE.SERVICES.FIRMWARE.1600_1800_APPLIANCE')}>
                                    <SMCOption value="no firmware configured">Do_Not_Upgrade</SMCOption>
                                    {
                                        availableFirmwares?.firmwareV2V3List?.map(firm => (
                                            <SMCOption value={firm[FirmwareGetListSchema.name].toLowerCase()} key={firm[FirmwareGetListSchema.name]}>{firm[FirmwareGetListSchema.name]}</SMCOption>
                                        ))
                                    }
                                </SMCSelect>
                            </SMCDisabledProvider>
                        </Indentation>
                        <SMCRadio value={modeValues.latest} label={t('SMP.GATEWAYS.PAGE.SERVICES.FIRMWARE.CP_LATEST')} />
                        <SMCRadio value={modeValues.local} label={t('SMP.GATEWAYS.PAGE.SERVICES.FIRMWARE.LINE3')} />
                    </SMCRadioGroup>
                </Indentation>
                <EmptyRow />
                <SMCLabel bold>{t('SMP.GATEWAYS.PAGE.SERVICES.FIRMWARE.LINE5')}</SMCLabel>
                <Indentation>
                    {/* Disable time choice if mode is local */}
                    <SMCDisabledProvider forceDisable={mode === modeValues.local || lockedToPlan}>
                        <SMCRadioGroup disabled={mode === modeValues.local} value={frequency} onChange={e => setPageValue(pageSchema.frequency, e.target.value)}>
                            <SMCRadio value={frequencyValues.immediately} label={t('SMP.GATEWAYS.PAGE.SERVICES.FIRMWARE.UPGRADE_IMMEDIATELY')} />
                            <SMCRadio value={frequencyValues.daily} label={t('SMP.GATEWAYS.PAGE.SERVICES.FIRMWARE.DAILY')} />
                            {
                                frequency === frequencyValues.daily && (
                                    <Indentation>
                                        <SMCSelect width={130} value={time} onChange={e => setPageValue(pageSchema.time, parseInt(e.target.value))} label={t('SMP.GATEWAYS.PAGE.SERVICES.FIRMWARE.TIME_OF_DAY')}>
                                            {timeOptions}
                                        </SMCSelect>
                                    </Indentation>
                                )
                            }
                            <SMCRadio value={frequencyValues.weekly} label={t('SMP.GATEWAYS.PAGE.SERVICES.FIRMWARE.WEEKLY')} />
                            {
                                frequency === frequencyValues.weekly && (
                                    <Indentation>
                                        <SMCSelect width={130} value={weekDay} onChange={e => setPageValue(pageSchema.weekDay, parseInt(e.target.value))} label={t('SMP.GATEWAYS.PAGE.SERVICES.FIRMWARE.DAY_OF_WEEK')}>
                                            {dayOfWeekOptions}
                                        </SMCSelect>
                                        <SMCSelect width={130} value={time} onChange={e => setPageValue(pageSchema.time, parseInt(e.target.value))} label={t('SMP.GATEWAYS.PAGE.SERVICES.FIRMWARE.TIME_OF_DAY')}>
                                            {timeOptions}
                                        </SMCSelect>
                                    </Indentation>
                                )
                            }
                            <SMCRadio value={frequencyValues.monthly} label={t('SMP.GATEWAYS.PAGE.SERVICES.FIRMWARE.MONTHLY')} />
                            {
                                frequency === frequencyValues.monthly && (
                                    <Indentation>
                                        <SMCSelect width={130} value={monthDay} onChange={e => setPageValue(pageSchema.monthDay, parseInt(e.target.value))} label={t('SMP.GATEWAYS.PAGE.SERVICES.FIRMWARE.DAY_OF_MONTH')}>
                                            {dayOfMonthOptions}
                                        </SMCSelect>
                                        <SMCSelect width={130} value={time} onChange={e => setPageValue(pageSchema.time, parseInt(e.target.value))} label={t('SMP.GATEWAYS.PAGE.SERVICES.FIRMWARE.TIME_OF_DAY')}>
                                            {timeOptions}
                                        </SMCSelect>
                                    </Indentation>
                                )
                            }
                        </SMCRadioGroup>
                    </SMCDisabledProvider>
                </Indentation>
                <Sepperator />
                <SMCTextInput
                    readOnly
                    value={reportedFirmware}
                    label={t('SMP.GATEWAYS.PAGE.SERVICES.FIRMWARE.REPORTED_FIRMWARE')}
                />
            </EditorLayout>
        </LockToPlanContainer>
    );
}

export default GatewayFirmware;